import {
    call, delay, fork, put, select, take,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { selectIsConnected, selectSocket } from '../../../state/socket/socketSlice';
import { selectUserCredentials } from '../../../state/userCredential/userCredentialSlice';
import { selectContact } from '../../../state/contact/contactSlice';
import { fetchVoicemailSaga } from '../../voicemail/fetchVoicemail/fetchVoicemail';
import { selectDevice } from '../../../state/device/deviceSlice';
import { incomingMessageSaga } from '../../message/incomingMessage/incomingMessage';
import { showSnackbar } from '../../../state/snackbar/snackbarSlice';
import { setUserPresence, setSelfPresence } from '../../../state/presence/presenceSlice';
import { selectLiveCallUpdatesEnabled, updateActiveDomainCall } from '../../../state/callCenter/activeDomainCall';
import { updateConferenceParticipantListAction } from '../../conferenceCall/updateConferenceParticipantList/action';
import { updateParkedCall } from '../../../state/callPark/callParkSlice';
import { getDisplayName } from '../../../utils/contactUtils';

const SOCKET_MESSAGE_AGENT = 'SOCKET_MESSAGE_AGENT';
const SOCKET_MESSAGE_AGENT_DEVICE = 'SOCKET_MESSAGE_AGENT_DEVICE';
const SOCKET_MESSAGE_CALL = 'SOCKET_MESSAGE_CALL';
const SOCKET_MESSAGE_CALL_QUEUE = 'SOCKET_MESSAGE_CALL_QUEUE';
const SOCKET_MESSAGE_CHAT_AVAILABLE = 'SOCKET_MESSAGE_CHAT_AVAILABLE';
const SOCKET_MESSAGE_CHAT_LEAVE = 'SOCKET_MESSAGE_CHAT_LEAVE';
const SOCKET_MESSAGE_CONTACTS_DOMAIN = 'SOCKET_MESSAGE_CONTACTS_DOMAIN';
const SOCKET_MESSAGE_CALL_DOMAIN = 'SOCKET_MESSAGE_CALL_DOMAIN';
const SOCKET_MESSAGE_UC_MESSAGE = 'SOCKET_MESSAGE_UC_MESSAGE';
const SOCKET_MESSAGE_QUEUE = 'SOCKET_MESSAGE_QUEUE';
const SOCKET_MESSAGE_QUEUED = 'SOCKET_MESSAGE_QUEUED';
const SOCKET_MESSAGE_VOICEMAIL = 'SOCKET_MESSAGE_VOICEMAIL';
const SOCKET_MESSAGE_CALL_UNMUTE = 'SOCKET_MESSAGE_CALL_UNMUTE';

export default function* socketListenersSaga() {

    let isConnected = yield select(selectIsConnected);
    const { uid, user } = yield select(selectUserCredentials);
    const { DeviceUser } = yield select(selectDevice);

    while (!isConnected) {
        yield delay(50);
        isConnected = yield select(selectIsConnected);
    }
    const socket = yield select(selectSocket);
    const channel = yield eventChannel((emitter) => {
        socket.on('agent', (data) => {
            emitter({ type: SOCKET_MESSAGE_AGENT, data });
        });
        socket.on('agent-device', (data) => {
            emitter({ type: SOCKET_MESSAGE_AGENT_DEVICE, data });
        });

        socket.on('call', (data) => {
            emitter({ type: SOCKET_MESSAGE_CALL, data });
        });

        socket.on('call-queue', (data) => {
            emitter({ type: SOCKET_MESSAGE_CALL_QUEUE, data });
        });

        socket.on('chatavailable', (data) => {
            emitter({ type: SOCKET_MESSAGE_CHAT_AVAILABLE, data });
        });

        socket.on('chat_leave', (data) => {
            emitter({ type: SOCKET_MESSAGE_CHAT_LEAVE, data });
        });

        socket.on('contacts-domain', (data) => {
            emitter({ type: SOCKET_MESSAGE_CONTACTS_DOMAIN, data });
        });

        socket.on('call-domain', (data) => {
            emitter({ type: SOCKET_MESSAGE_CALL_DOMAIN, data });
        });

        socket.on('uc-mesg', (data) => {
            emitter({ type: SOCKET_MESSAGE_UC_MESSAGE, data });
        });

        socket.on('queue', (data) => {
            emitter({ type: SOCKET_MESSAGE_QUEUE, data });
        });

        socket.on('queued', (data) => {
            emitter({ type: SOCKET_MESSAGE_QUEUED, data });
        });

        socket.on('voicemail', (data) => {
            emitter({ type: SOCKET_MESSAGE_VOICEMAIL, data });
        });

        socket.on('call_unmute', (data) => {
            emitter({ type: SOCKET_MESSAGE_CALL_UNMUTE, data });
        });
        return () => { };
    });
    while (true) {
        const res = yield take(channel);
        try {
            switch (res.type) {
                case SOCKET_MESSAGE_AGENT: {
                    break;
                }

                case SOCKET_MESSAGE_AGENT_DEVICE: {
                    break;
                }

                case SOCKET_MESSAGE_CALL: {
                    if (res.data.dnis && res.data.dnis.includes('adhoc.monitor.')) {
                        const participant = res.data.orig_from_user;
                        const conferenceUri = res.data.term_uri;
                        // if (res.data.remove === 'yes' && res.data.by_action === 'spawn_term') {
                        if (res.data.remove === 'yes') {
                            yield put(updateConferenceParticipantListAction({ conferenceUri }))
                            if (participant !== DeviceUser) {
                                console.log(`Participant ${participant} has left the conference: ${conferenceUri}.`);
                                const { contact } = yield select(selectContact);
                                const displayName = getDisplayName(contact, participant.replace(/\D/g, ''));

                                yield put(showSnackbar({
                                    message: `${displayName} has left the conference call.`,
                                    severity: "info"
                                }))
                            }
                        }
                    }
                    break;
                }

                case SOCKET_MESSAGE_CALL_QUEUE: {
                    if (res.data.orig_call_info === 'held') {
                        yield put(updateParkedCall({
                            parkSpot: res.data.term_user,
                            callData: res.data,
                            remove: res.data.remove === 'yes'
                        }));
                    }
                    break;
                }

                case SOCKET_MESSAGE_CHAT_AVAILABLE: {
                    break;
                }

                case SOCKET_MESSAGE_CHAT_LEAVE: {
                    break;
                }

                case SOCKET_MESSAGE_CONTACTS_DOMAIN: {
                    if (res.data.user === user) {
                        yield put(setSelfPresence(res.data.presence));
                    }
                    yield put(setUserPresence({ user: res.data.user, presence: res.data.presence }));
                    break;
                }

                case SOCKET_MESSAGE_CALL_DOMAIN: {
                    const liveCallUpdatesEnabled = yield select(selectLiveCallUpdatesEnabled);
                    if (liveCallUpdatesEnabled) {

                        //Preprocessing data to match existing format in slice
                        const normalizedCallData = {
                            session_id: res.data.sessionId,
                            orig_uri: res.data.orig_from_uri,
                            ...res.data
                        };

                        yield put(updateActiveDomainCall(normalizedCallData));
                    }
                    break;
                }

                case SOCKET_MESSAGE_UC_MESSAGE: {
                    const newMessage = res.data;
                    console.log('[VERBOSE] Event received: uc-mesg', newMessage)

                    // Define message types that should always be processed
                    const alwaysProcessTypes = [
                        'chat-name-change',
                        'chat-participant-leave',
                        'chat-participant-remove',
                        'chat-participant-remove-notification',
                        'chat-participant-add'
                    ];
                    const isSpecialType = alwaysProcessTypes.includes(newMessage.type);
                    // Handle term_uid that might be a string array (For group-chat and group-mms)
                    let termUid = newMessage.term_uid;
                    if (termUid && termUid.startsWith('[') && termUid.endsWith(']')) {
                        // Parse the string array and get the first element
                        try {
                            termUid = JSON.parse(termUid).includes(uid) ? uid : null;
                        } catch (e) {
                            console.error('Error parsing term_uid:', e);
                        }
                    }

                    // For chat-participant-leave, use text instead of from_uid
                    const relevantUid = ['chat-participant-leave', 'chat-participant-remove', 'chat-participant-remove-notification', 'chat-participant-add'].includes(newMessage.type)
                        ? newMessage.text
                        : newMessage.from_uid;

                    if ((isSpecialType && relevantUid !== uid) ||
                        (termUid && termUid == uid)) {
                        yield fork(incomingMessageSaga, newMessage);
                    }
                    break;
                }

                case SOCKET_MESSAGE_QUEUED: {
                    break;
                }

                case SOCKET_MESSAGE_QUEUE: {
                    break;
                }


                case SOCKET_MESSAGE_VOICEMAIL: {
                    yield call(fetchVoicemailSaga)
                    break;
                }

                case SOCKET_MESSAGE_CALL_UNMUTE: {
                    break;
                }

                default:

            }

        } catch (e) {
            console.error(e);
        }

    }

}